/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useMemo, useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import FeatherIcon from 'feather-icons-react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import productSearchService from 'service/productSearch';
import { showAlert } from 'store/alert/alert.thunk';
import Button from 'components/base/Button';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';
import installmentService from 'service/installment';

export type SearchProduct = {
  name: string;
  variation: string;
  barcode: string;
  original_barcode: string;
  unit: string;
};
const InstallmentAddTable = () => {
  const { t, i18n } = useTranslation();
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const dispatch: Dispatch<any> = useDispatch();
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 400);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const { data, isFetching, isLoading, refetch } = useQuery(
    ['GET_PRODUCT', currentPage, debouncedSearchValue],
    () => {
      const res = productSearchService
        .getListProduct({
          offset: pageToOffset(currentPage, 10),
          limit: 10,
          search: debouncedSearchValue
        })
        .then((res: any) => {
          setPageCount(Math.ceil(res?.count / 10));
          return res?.products;
        });
      return res;
    },
    {
      enabled:
        !!currentPage ||
        !debouncedSearchValue ||
        debouncedSearchValue?.length > 2
    }
  );

  const loadingData = isLoading || isFetching;

  const generateData: SearchProduct[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        return {
          name: items?.name,
          variation: items?.nomenclature?.is_variation ? t('yes') : t('no'),
          barcode: items?.barcode,
          original_barcode: items?.nomenclature?.barcode_original,
          unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          quantity: items?.total_quantity,
          currency_pid: items?.currency_pid || 0,
          total_quantity: items?.total_quantity,
          price: items?.price || 0,
          price_in: items?.price_in ?? 0,
          legal_entity_pid: items?.legal_entity_pid || 0,
          guid: items?.id,
          gupid: items?.pid
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const handleSubmit = (row: any) => {
    const createdData = {
      barcode: row?.barcode,
      currency_pid: row.currency_pid,
      price: row.price || 0,
      legal_entity_pid: row.legal_entity_pid,
      name: row.name,
      pid: row?.gupid,
      status: true
    };
    /* prettier-ignore */
    if (createdData) {
      setSubmitLoading(true);
         installmentService
           .createInstallment(createdData)
           .then((res: any) => {
             if (res) {
               dispatch(
                 showAlert({
                   title: t('product_successfully_saved'),
                   type: 'success'
                 })
               );
             }
             refetch();
            setSubmitLoading(false);
            if (barcodeInputRef.current) {
              barcodeInputRef.current.focus();
              setTimeout(() => barcodeInputRef.current?.select(), 500);
            }
           });
        }
  };

  const searchProductTableColumns: ColumnDef<SearchProduct>[] = [
    {
      id: 'action',
      cell: rowData => (
        <div>
          <Button
            variant="hover"
            onClick={() => handleSubmit(rowData.row.original)}
          >
            <FeatherIcon icon="plus" className="cursor-pointer" size={18} />
          </Button>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'original_barcode',
      header: t('barcode_original'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price',
      header: t('selling_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price_in',
      header: t('price_admission'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateData,
    columns: searchProductTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: false,
    selection: false
  });

  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
      setTimeout(() => barcodeInputRef.current?.select(), 500);
    }
  }, []);

  return (
    <>
      <div className="d-flex gap-2 mb-3 align-items-center">
        <div className="form-icon-container w-30">
          <Form.Floating>
            <Form.Control
              ref={barcodeInputRef}
              type="text"
              placeholder={t('enter_product')}
              onFocus={e => e.target.select()}
              onChange={e => setSearchValue(e?.target?.value)}
              className="form-icon-input"
            />
            <label
              htmlFor="floatingInputCustom"
              className="form-icon-label text-700"
            >
              {t('enter_product')}
            </label>
          </Form.Floating>
          <FontAwesomeIcon
            icon={faSearch}
            className="text-900 fs-9 form-icon"
          />
        </div>
      </div>
      <AdvanceTableProvider {...table}>
        <div className="position-relative">
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loadingData}
          />
          <AdvanceTableCPagination
            count={pageCount}
            page={currentPage}
            setCurrentPage={setCurrentPage}
          />
          {submitLoading && (
            <div className="loading-table">
              <span>
                <Spinner
                  animation="border"
                  role="status"
                  style={{ color: '#8200BF', width: '3rem', height: '3rem' }}
                ></Spinner>
              </span>
            </div>
          )}
        </div>
      </AdvanceTableProvider>
    </>
  );
};

export default InstallmentAddTable;
